import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useSubscription } from "./useSubscription";
import type { Plan } from "../types/payments";

/**
 * Hook to handle payment success modal display and subscription refresh
 *
 * - Checks for payment_success query param
 * - Extracts the verified plan from URL (passed by PaymentSuccessHandler)
 * - Triggers subscription data refetch
 * - Manages modal state
 *
 * Usage:
 * const { showModal, planTier, closeModal } = usePaymentSuccess();
 *
 * return (
 *   <>
 *     <YourPageContent />
 *     <PaymentSuccessModal isOpen={showModal} onClose={closeModal} planTier={planTier} />
 *   </>
 * );
 */
export function usePaymentSuccess() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showModal, setShowModal] = useState(false);
  const [planTier, setPlanTier] = useState<Plan | null>(null);
  const { refetch } = useSubscription();

  useEffect(() => {
    const paymentSuccess = searchParams.get("payment_success");
    const plan = searchParams.get("plan") as Plan | null;

    if (paymentSuccess === "true") {
      // Set the plan from URL if available
      if (plan && ["basic", "pro", "enterprise"].includes(plan)) {
        setPlanTier(plan);
      }

      // Trigger subscription data refetch to get fresh data
      refetch();

      // Show the modal
      setShowModal(true);

      // Clear the query params from URL without triggering navigation
      searchParams.delete("payment_success");
      searchParams.delete("plan");
      searchParams.delete("session_id");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, refetch]);

  const closeModal = useCallback(() => {
    setShowModal(false);
    setPlanTier(null);
  }, []);

  return {
    showModal,
    planTier,
    closeModal,
  };
}
